import { Fragment, Component } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { inject, observer } from 'mobx-react';
import MetaTags from 'react-meta-tags';
import socketIOClient from 'socket.io-client';

// Layouts
import AdminLayout from './layouts/Admin.jsx';

// Containers
import LoginPage from './containers/Auth/LoginPage';
import Verify2FAPage from './containers/Auth/Verify2FAPage';
import Setup2FAPage from './containers/Auth/Setup2FAPage';
import Recover2FAPage from './containers/Auth/Recover2FAPage';
import VisitorClientRegister from './containers/Admin/Visitors/VisitorClientRegister';
import VisitorAdd from './containers/Admin/Visitors/VisitorAdd';
import ForgotPasswordPage from './containers/Auth/ForgotPasswordPage';
import ResetPasswordPage from './containers/Auth/ResetPasswordPage';
import SignUpPage from './containers/Auth/SignUpPage';
import VisitorPublicRegister from './containers/Admin/Visitors/VisitorPublicRegister';

// Components
import withLocalization from '~/hoc/withLocalization';
import applicationRouter from '~/hoc/applicationRouter';
import LoginAsGuest from './containers/Admin/Users/LoginAsGuest.js'
import {
    Home,
    VisitorRegister,
    About,
    Features,
    TimeRegister,
    SchedulePlan,
    DigitalSignature,
    EmployeeGuide,
    Checklist,
    Communication,
    Documents,
    Deviation,
    VacationAndAbsence,
    Reports,
    Contact,
    UserTerms,
    PrivacyPolicy
} from "./containers/Landing/pages";

//public page components
import DocumentReport from './containers/Admin/Document/DocumentTemplate/DocumentReport';
import ReviewAndSignDoc from './containers/Admin/SignDocs/Recipients/public';
import SignDocPdf from './containers/Admin/SignDocs/SignDocPdf';
import MultipleDocumentsPdfPreview from './containers/Admin/Document/DocumentTemplate/MultipleDocumentsPdfPreview.js';
import SignSuccess from './containers/Admin/SignDocs/SignSuccess.js';
import DocExpire from './containers/Admin/SignDocs/DocExpire.js';
import DocDeleted from './containers/Admin/SignDocs/DocDeleted.js';
import ContractPDF from './containers/Admin/DocContracts/ContractPDF.js';
import TempPage from './containers/Admin/Monitoring/TempPage.js';
import UserRegister from './containers/Admin/Users/UserRegister.js';
import PasswordSetup from './containers/Admin/Users/PasswordSetup.js';
import MultiAccountAdministrationEmailVerification from './containers/Admin/Users/MultiAccountAdminEmailVerification.js';

//configs
import { SOCKET_ROOT } from '~/library/config';

//styles
import "./styles/css/styles.css";
import GuestLayout from './layouts/GuestLayout.jsx';


function PrivateRoute({ children, userStore }) {
    if (window.sessionStorage.getItem('cusToken'))
        return <GuestLayout />
    const { currentUser } = userStore;
    return currentUser ? <>{children}</> : <Navigate to="/" />
}

class App extends Component {
    constructor(props) {
        super(props);
        this.props.router.navigate((location, action) => {
            try {
                if (document.documentElement?.classList.value === 'nav-open')
                    document.documentElement?.classList.toggle('nav-open');
            } catch (e) {
                console.error('no open element');
            }
            const { userStore, commonStore, chatStore } = this.props;
            commonStore.log({
                username: userStore.currentUser ? userStore.currentUser.username : '',
                url: location.pathname,
                action: 'page',
                details: {},
            });
            if (userStore.currentUser) {
                chatStore.leaveRoom(); // check for route change - if leave room
            }
            if (userStore.currentUser && userStore.currentUser.is_now_locked) {
                if (location.pathname.indexOf('/locked') < 0) {
                    this.props.router.navigate("/admin/locked")
                }
            }
        });
    }

    componentWillMount() {
        const { commonStore } = this.props;
        this.setSocket();
        if (!commonStore.token) {
            commonStore.setAppLoaded();
        }
    }

    initRevisionCheck() {
        const store = this.props.commonStore;
        const interval = setInterval(async () => {
            const rev = await store.getGitRev();

            if (!store.currentGitRev) {
                store.setGitRev(rev);
            } else if (rev !== store.currentGitRev) {
                alert('New version is available, please reload the page');
                clearInterval(interval);
            }
        }, 5 * 60 * 1000);
    }

    componentDidMount() {
        const { commonStore, userStore, location } = this.props;
        if (commonStore.token) {
            userStore
                .pullUser()
                .then(() => {
                    this.setSocket();
                })
                .finally(() => {
                    commonStore.setAppLoaded();
                    userStore.loadNotifications();
                    if (userStore.currentUser && userStore.currentUser.is_now_locked) {
                        if (location.pathname.indexOf('/locked') < 0) {
                            this.props.router.navigate("/admin/locked")
                        }
                    }
                });
        }

        this.initRevisionCheck();
    }

    setSocket() {
        const { chatStore, userStore } = this.props;
        const socket = socketIOClient(SOCKET_ROOT);
        socket.on('connect', (data) => {
            chatStore.setupIo(socket);
            userStore.setupIo(socket);
        });
        socket.on('connection_established', (data) => {
            chatStore.setupIo(socket);
            userStore.setupIo(socket);
        });
    }

    render() {
        const { commonStore, userStore } = this.props;
        let scale = 1;
        if (commonStore.appLoaded) {
            return (
                <Fragment>
                    <MetaTags>
                        <meta name="viewport" content={`width=device-width, initial-scale=${scale}`} />
                    </MetaTags>
                    <Routes>
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/login/verify-2fa" element={<Verify2FAPage />} />
                        <Route path="/login/setup-2fa" element={<Setup2FAPage />} />
                        <Route path="/login/recover-2fa" element={<Recover2FAPage />} />
                        <Route path="/visitorClientRegister" element={<VisitorClientRegister />} />
                        <Route path="/visitorRegister/:enc" element={<VisitorPublicRegister />} />
                        <Route path="/visitorRegister" element={<VisitorAdd />} />
                        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                        <Route path="/reset-password" element={<ResetPasswordPage />} />
                        <Route path="/signup" element={<SignUpPage />} />
                        <Route path="/privacy" element={<PrivacyPolicy />} />
                        <Route path="/terms" element={<UserTerms />} />
                        <Route path="/visitor-register" element={<VisitorRegister />} />
                        <Route path="/about-us" element={<About />} />
                        <Route path="/features" element={<Features />} />
                        <Route path="/time-register" element={<TimeRegister />} />
                        <Route path="/schedule-plan" element={<SchedulePlan />} />
                        <Route path="/digital-signature" element={<DigitalSignature />} />
                        <Route path="/employee-guide" element={<EmployeeGuide />} />
                        <Route path="/checklist" element={<Checklist />} />
                        <Route path="/communication" element={<Communication />} />
                        <Route path="/documents" element={<Documents />} />
                        <Route path="/deviation" element={<Deviation />} />
                        <Route path="/vacation-and-absence" element={<VacationAndAbsence />} />
                        <Route path="/reports" element={<Reports />} />
                        <Route path="/contact-us" element={<Contact />} />
                        <Route path="/docexpire" element={<DocExpire />} />
                        <Route path="/docdeleted" element={<DocDeleted />} />
                        <Route path="/signsuccess" element={<SignSuccess />} />
                        <Route path="/multipleDocumentsPdfPreview/*" element={<MultipleDocumentsPdfPreview />} />
                        <Route path="/documentReport/*" element={<DocumentReport />} />
                        <Route path="/signdoc/pdf/*" element={<SignDocPdf />} />
                        <Route path="/signdoc/*" element={<ReviewAndSignDoc />} />
                        <Route path="/contract/pdf/*" element={<ContractPDF />} />
                        <Route path="/monitor/report/*" element={<TempPage />} />
                        <Route path="/user-register/*" element={<UserRegister />} />
                        <Route path="/user-update-password/*" element={<PasswordSetup />} />
                        <Route path="/multiAccountAdministration/*" element={<MultiAccountAdministrationEmailVerification />} />
                        <Route path='/loginAsGuest' element={<LoginAsGuest />} />
                        <Route path="/guest/*" element={<PrivateRoute><GuestLayout /></PrivateRoute>} />
                        <Route path="/admin/*" element={<PrivateRoute userStore={userStore}><AdminLayout /></PrivateRoute>} />
                        <Route path="*" element={<Home />} />
                    </Routes>
                </Fragment>
            );
        }
        return null;
    }
}

export default inject('userStore', 'commonStore', 'chatStore')(withLocalization(applicationRouter(observer(App))));
